import React, {
  createContext,
  useContext,
} from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from '../lib/offlineFirestore';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import {
  getDoc as firebaseGetDoc,
  getDocs as firebaseGetDocs,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import {
  getFunctions,
  httpsCallable,
} from 'firebase/functions';

import { auth, db } from '../config/firebase';
import { COLLECTIONS } from '../constants/collections';
import localAuthService from '../services/localAuthService';
import serverModeService from '../services/serverModeService';

const AuthContext = createContext(null);
const FUNCTIONS_REGION = 'us-central1';
const functions = getFunctions(getApp(), FUNCTIONS_REGION);

// Firebase accounts must be resolved against Firebase, regardless of stale
// offline-server settings. Local accounts continue using the LAN gateway.
const getAuthDoc = (ref) => auth.currentUser ? firebaseGetDoc(ref) : getDoc(ref);
const getAuthDocs = (ref) => auth.currentUser ? firebaseGetDocs(ref) : getDocs(ref);

const signOutLocalAndFirebase = async () => {
  await localAuthService.logout().catch(() => {});
  await signOut(auth).catch(() => {});
  serverModeService.setRuntimeBackend('cloud', '');
};

const safeCollectionName = (value, fallback) => {
  const clean =
    typeof value === 'string'
      ? value.trim()
      : '';

  return clean || fallback;
};

const USERS_COLLECTION =
  safeCollectionName(
    COLLECTIONS?.USERS,
    'users'
  );

const EMPLOYEES_COLLECTION =
  safeCollectionName(
    COLLECTIONS?.EMPLOYEES,
    'employees'
  );

const ROLES_COLLECTION =
  safeCollectionName(
    COLLECTIONS?.ROLES,
    'roles'
  );

const idValue = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const clean = value.trim();

    if (!clean) return '';

    return clean.includes('/')
      ? clean
          .split('/')
          .filter(Boolean)
          .pop() || ''
      : clean;
  }

  if (value?.id) {
    return String(value.id);
  }

  if (value?.path) {
    return String(value.path)
      .split('/')
      .filter(Boolean)
      .pop() || '';
  }

  return '';
};

const pollDocument = (reference, onNext, onError, intervalMs = 30000) => {
  let active = true;
  let timer = null;
  const run = async () => {
    if (!active) return;
    try {
      const snapshot = await getAuthDoc(reference);
      if (active) await onNext?.(snapshot);
    } catch (error) {
      if (active) onError?.(error);
    } finally {
      if (active) timer = setTimeout(run, intervalMs);
    }
  };
  timer = setTimeout(run, intervalMs);
  return () => {
    active = false;
    if (timer) clearTimeout(timer);
  };
};

const resolveRole = async (data) => {
  if (data?.role && typeof data.role === 'object' && data.role.name) {
    return {
      id: data.role.id || idValue(data.roleId),
      ...data.role,
    };
  }

  const roleId =
    idValue(data?.roleId);

  if (!roleId) {
    return null;
  }

  try {
    const snapshot =
      await getAuthDoc(
        doc(
          db,
          ROLES_COLLECTION,
          roleId
        )
      );

    return snapshot.exists()
      ? {
          id:
            snapshot.id,
          ...snapshot.data(),
        }
      : null;
  } catch (error) {
    console.warn(
      'Role lookup failed:',
      error
    );
    return null;
  }
};

const permissionList = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.flatMap(permissionList);
  }

  if (typeof value === 'string') {
    const clean = value.trim().toLowerCase();
    return clean ? [clean] : [];
  }

  if (typeof value === 'object') {
    if (value.permissions !== undefined) {
      return permissionList(value.permissions);
    }

    return Object.entries(value)
      .filter(([, enabled]) => enabled === true)
      .map(([key]) =>
        String(key || '')
          .trim()
          .toLowerCase()
      )
      .filter(Boolean);
  }

  return [];
};

const normalizePermissions = (
  data,
  role
) => [
  ...new Set([
    ...permissionList(data?.permissions),
    ...permissionList(role?.permissions),
    ...permissionList(data?.role?.permissions),
    ...permissionList(data?.rolePermissions),
    ...permissionList(data?.access?.permissions),
  ]),
];

const friendlyLoginError = (error, employee = false) => {
  const code = String(error?.code || '').toLowerCase();

  if (
    code.includes('invalid-credential') ||
    code.includes('wrong-password') ||
    code.includes('user-not-found') ||
    code.includes('invalid-email')
  ) {
    return employee
      ? 'Invalid employee ID/email or password.'
      : 'Invalid email or password.';
  }

  if (code.includes('user-disabled')) {
    return employee
      ? 'This employee login is disabled. Contact your administrator.'
      : 'This account is disabled. Contact your administrator.';
  }

  if (code.includes('too-many-requests')) {
    return 'Too many login attempts. Please try again later.';
  }

  if (code.includes('network-request-failed')) {
    return 'Unable to connect to the login service. Check your internet connection and try again.';
  }

  return employee
    ? 'Employee login failed. Please check your credentials and try again.'
    : 'Login failed. Please check your credentials and try again.';
};

const employeeBlocked = (data) => {
  const status =
    String(
      data?.status || ''
    ).toLowerCase();

  return (
    data?.isBlocked === true ||
    data?.loginEnabled === false ||
    data?.isActive === false ||
    [
      'terminated',
      'suspended',
      'inactive',
      'blocked',
      'disabled',
    ].includes(status)
  );
};

export const AuthProvider = ({
  children,
}) => {
  const [user, setUser] =
    React.useState(null);

  const [userData, setUserData] =
    React.useState(null);

  const [roleData, setRoleData] =
    React.useState(null);

  const [permissions, setPermissions] =
    React.useState([]);

  const [loading, setLoading] =
    React.useState(true);

  const [authError, setAuthError] =
    React.useState('');

  const [isSuperAdmin, setIsSuperAdmin] =
    React.useState(false);

  const [isEmployee, setIsEmployee] =
    React.useState(false);

  const employeeUnsubscribeRef =
    React.useRef(null);

  const userUnsubscribeRef =
    React.useRef(null);

  const clearProfileListeners =
    React.useCallback(() => {
      if (
        typeof userUnsubscribeRef.current ===
        'function'
      ) {
        userUnsubscribeRef.current();
      }

      if (
        typeof employeeUnsubscribeRef.current ===
        'function'
      ) {
        employeeUnsubscribeRef.current();
      }

      userUnsubscribeRef.current = null;
      employeeUnsubscribeRef.current = null;
    }, []);

  const applyPrincipal =
    React.useCallback(
      async (
        firebaseUser,
        data,
        source = 'user'
      ) => {
        if (!data) {
          setUserData(null);
          setRoleData(null);
          setPermissions([]);
          setIsEmployee(false);
          setIsSuperAdmin(false);
          return;
        }

        const role =
          await resolveRole(data);

        if (
          role?.active === false ||
          role?.isActive === false
        ) {
          setAuthError(
            'Your assigned role is disabled.'
          );

          await signOutLocalAndFirebase();
          return;
        }

        if (
          source === 'employee' &&
          employeeBlocked(data)
        ) {
          setAuthError(
            'Your employee account is blocked or inactive. Contact your administrator.'
          );

          await signOutLocalAndFirebase();
          return;
        }

        const resolvedPermissions =
          Array.from(new Set([
            ...normalizePermissions(
              data,
              role
            ),
            // Every authenticated company user may mark their own attendance
            // through the Selfie Attendance flow. Administrative attendance
            // viewing/editing remains protected by the separate HR permissions.
            'attendance_selfie',
          ]));

        const normalizedRoleKey =
          String(
            role?.key ||
              role?.code ||
              role?.name ||
              ''
          )
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '');

        // IMPORTANT:
        // "all" means full permissions INSIDE the assigned company role.
        // It must not turn Company Owner / Company Admin into a Super Admin.
        const superAdmin =
          Boolean(
            data?.metadata?.isSuperAdmin
          ) ||
          normalizedRoleKey ===
            'super_admin' ||
          idValue(data?.roleId) ===
            'role_super_admin';

        const tenantId =
          idValue(data?.tenantId) ||
          idValue(data?.tenantIdValue) ||
          idValue(data?.tenant) ||
          idValue(data?.companyId) ||
          idValue(data?.companyIdValue) ||
          idValue(data?.company) ||
          idValue(data?.tenantRef) ||
          idValue(data?.companyRef) ||
          '';

        const normalized = {
          ...data,
          uid:
            firebaseUser?.uid ||
            data.uid ||
            '',
          id:
            source === 'employee'
              ? (
                  idValue(data?.employeeRef) ||
                  idValue(data?.employeeId) ||
                  data.id ||
                  firebaseUser?.uid ||
                  ''
                )
              : (
                  data.id ||
                  firebaseUser?.uid ||
                  ''
                ),
          employeeRecordId:
            source === 'employee'
              ? (
                  idValue(data?.employeeRef) ||
                  idValue(data?.employeeId) ||
                  data.id ||
                  ''
                )
              : '',
          tenantId,
          tenantIdValue:
            tenantId,
          roleId:
            idValue(
              data.roleId
            ),
          role,
          permissions:
            resolvedPermissions,
          principalType:
            source,
        };

        setUserData(
          normalized
        );

        setRoleData(role);
        setPermissions(
          resolvedPermissions
        );

        setIsEmployee(
          source ===
            'employee' ||
            Boolean(
              data?.employeeId ||
                data?.employeeRef
            )
        );

        setIsSuperAdmin(
          superAdmin
        );

        setAuthError('');
      },
      []
    );

  const findEmployeeByAuthUser =
    React.useCallback(
      async (
        firebaseUser
      ) => {
        if (!firebaseUser) {
          return null;
        }

        // Preferred schema: employees.authUid == Firebase Auth UID.
        try {
          const snapshot =
            await getAuthDocs(
              query(
                collection(
                  db,
                  EMPLOYEES_COLLECTION
                ),
                where(
                  'authUid',
                  '==',
                  firebaseUser.uid
                ),
                limit(1)
              )
            );

          if (
            !snapshot.empty
          ) {
            const row =
              snapshot.docs[0];

            return {
              id:
                row.id,
              ...row.data(),
            };
          }
        } catch (error) {
          console.warn(
            'Employee authUid lookup failed:',
            error
          );
        }

        // Legacy schema: employee document ID equals auth UID.
        try {
          const snapshot =
            await getAuthDoc(
              doc(
                db,
                EMPLOYEES_COLLECTION,
                firebaseUser.uid
              )
            );

          if (
            snapshot.exists()
          ) {
            return {
              id:
                snapshot.id,
              ...snapshot.data(),
            };
          }
        } catch (error) {
          console.warn(
            'Legacy employee UID lookup failed:',
            error
          );
        }

        // Last compatibility lookup by email.
        if (
          firebaseUser.email
        ) {
          try {
            const snapshot =
              await getAuthDocs(
                query(
                  collection(
                    db,
                    EMPLOYEES_COLLECTION
                  ),
                  where(
                    'email',
                    '==',
                    String(
                      firebaseUser.email
                    )
                      .trim()
                      .toLowerCase()
                  ),
                  limit(1)
                )
              );

            if (
              !snapshot.empty
            ) {
              const row =
                snapshot.docs[0];

              return {
                id:
                  row.id,
                ...row.data(),
              };
            }
          } catch (error) {
            console.warn(
              'Employee email lookup failed:',
              error
            );
          }
        }

        return null;
      },
      []
    );

  const loadPrincipal =
    React.useCallback(
      async (
        firebaseUser
      ) => {
        clearProfileListeners();

        if (!firebaseUser) {
          setUserData(null);
          setRoleData(null);
          setPermissions([]);
          setIsEmployee(false);
          setIsSuperAdmin(false);
          return;
        }

        if (firebaseUser.isLocalOffline) {
          // A LAN login can be valid without Firebase connectivity. Use the
          // server-approved employee profile rather than requiring a cloud
          // users/{uid} read, but verify the session and approved device with
          // the actual paired tenant first.
          const session = localAuthService.getSession();
          const status = await serverModeService.getLocalStatus();
          const sessionId = String(session?.user?.uid || session?.user?.id || '');
          if (!session?.tenantId || sessionId !== String(firebaseUser.uid || '') ||
              !status?.server?.claimed || String(status.server.tenantId || '') !== String(session.tenantId)) {
            setAuthError('The LAN login session has expired or this device still requires approval.');
            await signOutLocalAndFirebase();
            return;
          }
          const localProfile = { ...(session.user || {}), uid: sessionId, tenantId: session.tenantId, tenantIdValue: session.tenantId };
          await applyPrincipal(firebaseUser, localProfile,
            localProfile.source === 'employee' || localProfile.employeeId || localProfile.employeeRef ? 'employee' : 'user');
          return;
        }

        const userRef =
          doc(
            db,
            USERS_COLLECTION,
            firebaseUser.uid
          );

        try {
          const userSnapshot =
            await getAuthDoc(
              userRef
            );

          if (
            userSnapshot.exists()
          ) {
            const rawUserData = {
              id:
                userSnapshot.id,
              ...userSnapshot.data(),
            };

            const employeeMarker =
              rawUserData?.source === 'employee' ||
              rawUserData?.employeeId ||
              rawUserData?.employeeRef;

            let employeeRecord = null;

            if (employeeMarker) {
              const employeeId =
                idValue(rawUserData.employeeRef) ||
                idValue(rawUserData.employeeId);

              if (employeeId) {
                try {
                  const employeeSnapshot =
                    await getAuthDoc(
                      doc(
                        db,
                        EMPLOYEES_COLLECTION,
                        employeeId
                      )
                    );

                  if (employeeSnapshot.exists()) {
                    employeeRecord = {
                      id: employeeSnapshot.id,
                      ...employeeSnapshot.data(),
                    };
                  }
                } catch (error) {
                  console.warn(
                    'Linked employee profile lookup failed:',
                    error
                  );
                }
              }
            }

            // Compatibility:
            // Some older employee user mirrors contain roleId/tenantId only,
            // without source="employee" or employeeRef. Resolve the employee
            // record by authUid / document ID / email before treating the
            // account as a normal company user.
            if (
              !employeeRecord &&
              rawUserData?.metadata?.isSuperAdmin !== true
            ) {
              try {
                employeeRecord =
                  await findEmployeeByAuthUser(
                    firebaseUser
                  );
              } catch (error) {
                console.warn(
                  'Employee compatibility lookup failed:',
                  error
                );
              }
            }

            const isEmployeePrincipal =
              Boolean(
                employeeMarker ||
                employeeRecord
              );

            const principalData =
              employeeRecord
                ? {
                    ...rawUserData,
                    ...employeeRecord,
                    employeeId: employeeRecord.id,
                    employeeRef:
                      rawUserData.employeeRef ||
                      employeeRecord.id,
                    authUid:
                      employeeRecord.authUid ||
                      firebaseUser.uid,
                    // User mirror owns the authoritative role/tenant link
                    // when present; employee values remain the fallback.
                    roleId:
                      rawUserData.roleId ||
                      employeeRecord.roleId,
                    tenantId:
                      rawUserData.tenantId ||
                      employeeRecord.tenantId,
                    tenantIdValue:
                      rawUserData.tenantIdValue ||
                      employeeRecord.tenantIdValue,
                  }
                : rawUserData;

            await applyPrincipal(
              firebaseUser,
              principalData,
              isEmployeePrincipal
                ? 'employee'
                : 'user'
            );

            userUnsubscribeRef.current =
              pollDocument(
                userRef,
                async (snapshot) => {
                  if (
                    snapshot.exists()
                  ) {
                    const next = {
                      id: snapshot.id,
                      ...snapshot.data(),
                    };

                    if (!isEmployeePrincipal) {
                      await applyPrincipal(
                        firebaseUser,
                        next,
                        'user'
                      );
                    }
                  }
                },
                (error) => {
                  console.error(
                    'User profile listener failed:',
                    error
                  );
                }
              );

            if (isEmployeePrincipal && employeeRecord?.id) {
              const employeeRef =
                doc(
                  db,
                  EMPLOYEES_COLLECTION,
                  employeeRecord.id
                );

              employeeUnsubscribeRef.current =
                pollDocument(
                  employeeRef,
                  async (snapshot) => {
                    if (!snapshot.exists()) {
                      setAuthError(
                        'Your employee profile no longer exists.'
                      );
                      await signOutLocalAndFirebase();
                      return;
                    }

                    const latestEmployee = {
                      id: snapshot.id,
                      ...snapshot.data(),
                    };

                    const latestUser =
                      await getAuthDoc(userRef);

                    const mirror =
                      latestUser.exists()
                        ? latestUser.data()
                        : rawUserData;

                    await applyPrincipal(
                      firebaseUser,
                      {
                        ...mirror,
                        ...latestEmployee,
                        employeeId:
                          latestEmployee.id,
                        employeeRef:
                          mirror.employeeRef ||
                          latestEmployee.id,
                        roleId:
                          mirror.roleId ||
                          latestEmployee.roleId,
                        tenantId:
                          mirror.tenantId ||
                          latestEmployee.tenantId,
                        tenantIdValue:
                          mirror.tenantIdValue ||
                          latestEmployee.tenantIdValue,
                      },
                      'employee'
                    );
                  },
                  (error) => {
                    console.error(
                      'Employee access listener failed:',
                      error
                    );
                  }
                );
            }

            return;
          }
        } catch (error) {
          console.warn(
            'users/{uid} lookup failed:',
            error
          );
        }

        const employee =
          await findEmployeeByAuthUser(
            firebaseUser
          );

        if (
          employee
        ) {
          await applyPrincipal(
            firebaseUser,
            employee,
            'employee'
          );

          const employeeRef =
            doc(
              db,
              EMPLOYEES_COLLECTION,
              employee.id
            );

          employeeUnsubscribeRef.current =
            pollDocument(
              employeeRef,
              async (snapshot) => {
                if (
                  snapshot.exists()
                ) {
                  await applyPrincipal(
                    firebaseUser,
                    {
                      id:
                        snapshot.id,
                      ...snapshot.data(),
                    },
                    'employee'
                  );
                }
              },
              (error) => {
                console.error(
                  'Employee profile listener failed:',
                  error
                );
              }
            );

          return;
        }

        setAuthError(
          'No application profile is linked to this Firebase account.'
        );

        await signOutLocalAndFirebase();
      },
      [
        applyPrincipal,
        clearProfileListeners,
        findEmployeeByAuthUser,
      ]
    );

  React.useEffect(() => {
    let active = true;

    const hydratePrincipal = async (firebaseUser = null) => {
      if (!active) return;
      const resolvedUser = firebaseUser || localAuthService.getUser();
      // An existing LAN setting is NOT permission to reroute Firebase sign-in.
      // Choose source from the verified identity, never from cached settings.
      serverModeService.setRuntimeBackend(resolvedUser?.isLocalOffline ? 'local' : 'cloud', resolvedUser?.uid || '');
      setLoading(true);
      setUser(resolvedUser);

      try {
        await loadPrincipal(resolvedUser);
      } catch (error) {
        console.error('Auth profile load failed:', error);
        setAuthError(error?.message || 'Unable to load account profile');
      } finally {
        if (active) setLoading(false);
      }
    };

    // When this React build is served by the LAN server, discover the local
    // server/tenant from the same origin before Firebase auth state settles.
    // This is additive; Firebase remains the primary auth provider whenever
    // it has an authenticated user.
    localAuthService.bootstrap().then(() => {
      if (active && !auth.currentUser && localAuthService.getUser()) {
        hydratePrincipal(null);
      }
    }).catch(() => {});

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      await hydratePrincipal(firebaseUser || null);
    });

    const unsubscribeLocal = localAuthService.subscribe(async (localUser) => {
      if (!active || auth.currentUser) return;
      await hydratePrincipal(localUser || null);
    });

    return () => {
      active = false;
      unsubscribe();
      unsubscribeLocal();
      clearProfileListeners();
    };
  }, [clearProfileListeners, loadPrincipal]);

  const login =
    async (
      email,
      password
    ) => {
      setAuthError('');
      const normalizedEmail = String(email || '').trim().toLowerCase();

      try {
        serverModeService.setRuntimeBackend('cloud');
        return await signInWithEmailAndPassword(auth, normalizedEmail, password);
      } catch (error) {
        const networkFailure = String(error?.code || '').includes('network-request-failed') ||
          (typeof navigator !== 'undefined' && navigator.onLine === false);

        if (networkFailure && serverModeService.isLocalConfigured()) {
          try {
            const localSession = await localAuthService.login(normalizedEmail, password);
            if (localSession.awaitingApproval) throw new Error(localSession.message || 'LAN device requires administrator approval.');
            const localUser = localSession.user;
            setUser(localUser);
            await loadPrincipal(localUser);
            return { user: localUser, local: true, session: localSession };
          } catch (localError) {
            console.warn('LAN offline login failed:', localError);
            error = localError;
          }
        }

        console.error('Account login failed:', error);
        const message = friendlyLoginError(error, false);
        setAuthError(message);
        const wrapped = new Error(message);
        wrapped.code = error?.code || 'auth/login-failed';
        throw wrapped;
      }
    };

  const loginEmployee =
    async (
      employeeCodeOrEmail,
      password
    ) => {
      setAuthError('');

      const identifier =
        String(
          employeeCodeOrEmail ||
            ''
        ).trim();

      const codeIdentifier = identifier.includes('@')
        ? identifier
        : identifier.toUpperCase();

      if (!identifier) {
        throw new Error(
          'Employee ID or email is required'
        );
      }

      let email =
        identifier.includes('@')
          ? identifier.toLowerCase()
          : '';

      if (!email) {
        // Preferred resolver: Cloud Function.
        try {
          const resolver =
            httpsCallable(
              functions,
              'resolveEmployeeLoginIdentifier'
            );

          const result =
            await resolver({
              identifier: codeIdentifier,
            });

          email =
            String(
              result?.data?.email ||
                ''
            )
              .trim()
              .toLowerCase();
        } catch (resolverError) {
          console.warn(
            'Employee identifier Cloud Function unavailable; trying Firestore fallback:',
            resolverError
          );
        }

        // Compatibility fallback for projects where the resolver function
        // has not been deployed yet.
        if (!email) {
          const fields = [
            'employeeCode',
            'employeeId',
            'code',
          ];

          for (const field of fields) {
            try {
              const snapshot =
                await getAuthDocs(
                  query(
                    collection(
                      db,
                      EMPLOYEES_COLLECTION
                    ),
                    where(
                      field,
                      '==',
                      codeIdentifier
                    ),
                    limit(1)
                  )
                );

              if (!snapshot.empty) {
                const employee =
                  snapshot.docs[0].data();

                email =
                  String(
                    employee?.email ||
                      employee?.loginEmail ||
                      ''
                  )
                    .trim()
                    .toLowerCase();

                if (email) break;
              }
            } catch (lookupError) {
              console.warn(
                `Employee ${field} lookup failed:`,
                lookupError
              );
            }
          }
        }

        if (!email) {
          throw new Error(
            'Employee account could not be found. Check the employee ID or use the registered email address.'
          );
        }
      }

      try {
        serverModeService.setRuntimeBackend('cloud');
        return await signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        const networkFailure = String(error?.code || '').includes('network-request-failed') ||
          (typeof navigator !== 'undefined' && navigator.onLine === false);

        if (networkFailure && serverModeService.isLocalConfigured()) {
          try {
            const localSession = await localAuthService.login(email, password);
            if (localSession.awaitingApproval) throw new Error(localSession.message || 'LAN device requires administrator approval.');
            const localUser = localSession.user;
            setUser(localUser);
            await loadPrincipal(localUser);
            return { user: localUser, local: true, session: localSession };
          } catch (localError) {
            console.warn('Employee LAN offline login failed:', localError);
            error = localError;
          }
        }

        console.error('Employee login failed:', error);
        const message = friendlyLoginError(error, true);
        setAuthError(message);
        const wrapped = new Error(message);
        wrapped.code = error?.code || 'auth/employee-login-failed';
        throw wrapped;
      }
    };

  const refreshUserData =
    async () => {
      const activeUser = auth.currentUser || localAuthService.getUser();
      if (activeUser) await loadPrincipal(activeUser);
    };

  const logout =
    async () => {
      clearProfileListeners();
      await signOutLocalAndFirebase();

      setUser(null);
      setUserData(null);
      setRoleData(null);
      setPermissions([]);
      setIsSuperAdmin(false);
      setIsEmployee(false);
    };

  const hasPermission =
    React.useCallback(
      (
        permission
      ) => {
        if (
          isSuperAdmin ||
          permissions.includes(
            'all'
          )
        ) {
          return true;
        }

        const key =
          String(
            permission ||
              ''
          )
            .trim()
            .toLowerCase();

        return (
          Boolean(key) &&
          permissions.includes(
            key
          )
        );
      },
      [
        isSuperAdmin,
        permissions,
      ]
    );

  const value =
    React.useMemo(
      () => ({
        user,
        userData,
        roleData,
        permissions,
        loading,
        authError,
        isSuperAdmin,
        isEmployee,
        isAuthenticated:
          Boolean(user),

        tenantId:
          userData?.tenantId ||
          userData?.tenantIdValue ||
          '',

        login,
        loginEmployee,
        logout,
        refreshUserData,
        hasPermission,
      }),
      [
        user,
        userData,
        roleData,
        permissions,
        loading,
        authError,
        isSuperAdmin,
        isEmployee,
        hasPermission,
      ]
    );

  /*
   * IMPORTANT:
   * AuthProvider must NEVER replace/unmount the complete CMS while profile
   * state is loading. It only exposes `loading` through context.
   *
   * ProtectedRoute is responsible for the genuine first-entry loader.
   * This keeps MainLayout, page backgrounds, animations, glass effects and
   * component state mounted during later auth/profile updates.
   */
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context =
    useContext(
      AuthContext
    );

  if (!context) {
    throw new Error(
      'useAuth must be used within an AuthProvider'
    );
  }

  return context;
};

export default AuthContext;